import React from 'react';
import { ArrayHelpers, Help, Loading, Paging, SystemText } from 'e4shared';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { apiGet } from 'services';
import BlobImage from 'components/blobImage';
import { UserMenu } from 'components/user/menu';
import WidgetBase from 'views/myDashboard/components/widgets/_base';
import { isPast, e4Format, e4Formats } from 'helpers/dates';

export const StaffHelpDetails = ({ staff, showObjectives }) => {
    const text = {
        objectives: SystemText('Home/Widgets/Team/Help/Objectives'),
        outstanding: SystemText('Home/Widgets/Team/Help/Outstanding'),
        overdue: SystemText('Home/Widgets/Team/Help/Overdue'),
        awaitingApproval: SystemText('Home/Widgets/Team/Help/AwaitingApproval'),
        scheduledReviews: SystemText('Home/Widgets/Team/Help/ScheduledReviews'),
        last: SystemText('Home/Widgets/Team/Help/Last'),
        next: SystemText('Home/Widgets/Team/Help/Next'),
    };

    return (
        <div className="home-team-help">
            {showObjectives && <label>{text.objectives}</label>}
            {showObjectives && (
                <div>
                    <span className="mr5">{text.outstanding}:</span>
                    <span className={staff.outstanding > 0 ? 'label label-primary' : ''}>{staff.outstanding}</span>
                </div>
            )}
            {showObjectives && (
                <div>
                    <span className="mr5">{text.overdue}:</span>
                    <span className={staff.overdue > 0 ? 'label label-danger' : ''}>{staff.overdue}</span>
                </div>
            )}
            {showObjectives && (
                <div>
                    <span className="mr5">{text.awaitingApproval}:</span>
                    <span className={staff.awaiting > 0 ? 'label label-warning' : ''}>{staff.awaiting}</span>
                </div>
            )}
            <label className="mt10">{text.scheduledReviews}</label>
            <div>
                <span className="mr5">{text.last}:</span>
                <span>{staff.lastReview ? e4Format(staff.lastReview) : '-'}</span>
            </div>
            <div>
                <span className="mr5">{text.next}:</span>
                <span className={staff.nextReview && isPast(staff.nextReview) ? 'label label-danger' : ''}>
                    {staff.nextReview ? e4Format(staff.nextReview) : '-'}
                </span>
            </div>
        </div>
    );
};

export const TeamList = ({ item, history, coach }) => {
    const loaded = React.useRef(null);
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [pagedData, setPagedData] = React.useState([]);
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(6);
    const recordCount = data.length;

    const user = useSelector(state => state.user.user);

    React.useEffect(() => {
        const url = coach ? 'profile-coaching' : `profile/${user.id}/team`;
        apiGet(url, user.guid, response => {
            setData(response.filter(r => r.active));
            setLoading(false);
        });
    }, []);

    React.useEffect(() => {
        setPagedData(ArrayHelpers.getPage(data, page, pageSize));
    }, [page]);

    React.useEffect(() => {
        const pageSizeGenerator = () => {
            const rowHeight = 40;
            const extrasHeight = 90;
            const thisHeight = 105 * item.h - 70;
            let result = Math.ceil((thisHeight - extrasHeight) / rowHeight);
            if (result < recordCount) result--;
            return result < 1 ? 1 : result;
        };

        const ps = pageSizeGenerator();
        setPagedData(ArrayHelpers.getPage(data, 1, ps));
        setPage(1);
        setPageSize(ps);
    }, [loaded.current, data, item.h, recordCount]);

    const text = {
        none: SystemText('Home/Widgets/Team/None'),
        staff: SystemText('Home/Widgets/Team/Staff'),
        jump: SystemText('Home/Widgets/Team/Jump'),
    };

    const chart = (
        <div id="home-team-list" ref={loaded}>
            {data.length === 0 ? (
                <div>
                    <p>{text.none}</p>
                </div>
            ) : (
                <div>
                    <table className="table table-condensed">
                        <tbody>
                            <tr>
                                <th width="1">#</th>
                                <th>{text.staff}</th>
                                <th width="1" className="text-right"></th>
                            </tr>
                            {pagedData.map((t, i) => {
                                let extra = 'fa-face-smile text-success';
                                if (t.awaiting) extra = 'fa-face-meh text-warning';
                                if (t.overdue > 0 || (t.nextReview && isPast(t.nextReview)))
                                    extra = 'fa-face-frown text-danger';

                                return (
                                    <tr key={t.id}>
                                        <td className="no-wrap" style={null}>
                                            {(page - 1) * pageSize + i + 1}.
                                        </td>
                                        <td className="no-wrap">
                                            <Link to={`/develop/performance-dashboard/${t.id}`} className="flex flex-center">
                                                <BlobImage
                                                    height="30"
                                                    className="img-circle mr10"
                                                    folder="avatar"
                                                    initials={t.initials}
                                                    key={t.avatar}
                                                    src={t.avatar}
                                                    alt={t.full_Name}
                                                />
                                                <div
                                                    style={{
                                                        maxWidth: '180px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {t.full_Name}
                                                </div>
                                            </Link>
                                        </td>
                                        <td style={null} className="no-wrap text-right">
                                            <div className="flex flex-center">
                                                <Help
                                                    target={<i className={`fa ${extra} help-lg`} />}
                                                    id={`user-help-${t.id}`}
                                                >
                                                    <StaffHelpDetails staff={t} showObjectives />
                                                </Help>
                                                <span className="ml5">
                                                    <UserMenu
                                                        history={history}
                                                        profile={t}
                                                        user={user}
                                                        small
                                                        label={text.jump}
                                                    />
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {recordCount > pageSize && (
                        <div className="text-center mt10">
                            <Paging
                                page={page}
                                narrow={item.w < 3}
                                pageSize={pageSize}
                                recordCount={recordCount}
                                goToPage={setPage}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <WidgetBase
            filters={null}
            chart={chart}
            item={item}
            id={'whats-happening'}
            help={null}
            hideConfig
            extra={null}
            loading={loading}
        />
    );
};

export default TeamList;
